import { useEffect, useState } from "react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDNumberInput from "../MDNumberInput"
import MDSelect from "components/MDSelect"
import MDTypography from "components/MDTypography"
import {
  useGetPurchaseSuppliersQuery,
  useUpdatePurchaseMutation,
  useUpdatePurchaseProposalPricesMutation,
} from "shared/redux/endpoints/procurementPurchases"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import {
  formatVND,
  formatDate,
  proposalLineKey,
  proposalLineRef,
  unpricedRows,
  MISSING_PRICE_REASON_LABEL,
  PROPOSAL_TAX_CODE_MAX,
} from "../../constants"

// A row with a quantity is priced per unit (BE multiplies); NO_QUANTITY rows
// can only take a line total.
const byUnit = (r) => Number(r.quantity) > 0

// Popup over GET /purchases/{id}/price-readiness: lists the selected
// proposal's unpriced rows and saves typed prices in one PUT
// /purchases/{id}/proposal-prices. A blank AUTO_FILLABLE row keeps its
// suggestion (the PUT fills it); a blank MISSING row stays missing.
export default function MissingPricesDialog({ open, onClose, po, readiness }) {
  const [values, setValues] = useState({})
  const [taxCode, setTaxCode] = useState("")
  const [vendorId, setVendorId] = useState(null)
  const [savePrices, { isLoading: saving }] = useUpdatePurchaseProposalPricesMutation()
  const [updatePurchase, { isLoading: savingVendor }] = useUpdatePurchaseMutation()
  const noVendor = readiness?.issue === "NO_VENDOR"
  const { data: suppliers } = useGetPurchaseSuppliersQuery(undefined, { skip: !open || !noVendor })
  const supplierOptions = (suppliers || []).map((s) => ({ value: s.id, label: s.name }))

  useEffect(() => {
    if (open) {
      setValues({})
      setTaxCode("")
    }
  }, [open])

  const rows = unpricedRows(readiness)
  const typed = rows.filter((r) => Number(values[proposalLineKey(r)]) > 0)
  const canSave =
    !saving &&
    taxCode.trim().length <= PROPOSAL_TAX_CODE_MAX &&
    (typed.length > 0 || !!taxCode.trim() || rows.some((r) => r.status === "AUTO_FILLABLE"))

  const handleSave = async () => {
    try {
      const next = await savePrices({
        id: po.id,
        items: typed.map((r) => {
          const value = Number(values[proposalLineKey(r)])
          return { ...proposalLineRef(r), ...(byUnit(r) ? { unitPrice: value } : { proposedAmount: value }) }
        }),
        taxCode: taxCode.trim() || undefined,
      }).unwrap()
      setValues({})
      if (next?.ready) {
        showToastSuccess("Đã đủ giá cho PO.")
        onClose()
      } else {
        showToastSuccess(`Đã lưu giá. Còn ${unpricedRows(next).length} dòng chưa có giá.`)
      }
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Không lưu được giá. Vui lòng thử lại."))
    }
  }

  const handleSaveVendor = async () => {
    try {
      await updatePurchase({ id: po.id, vendorId, paymentMethod: po.paymentMethod }).unwrap()
      showToastSuccess("Đã chọn NCC cho PO.")
    } catch (e) {
      // 409 = the PO already has a selected proposal; change vendor by selecting another quote.
      showToastError(getApiErrorMessage(e, "Không chọn được NCC. Vui lòng thử lại."))
    }
  }

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="md">
      <DialogTitle>Nhập giá còn thiếu</DialogTitle>
      <DialogContent sx={noVendor ? { minHeight: 540 } : undefined}>
        {noVendor ? (
          <MDBox pt={1} display="flex" flexDirection="column" gap={1.5}>
            <MDTypography variant="body2" color="text">
              PO chưa có nhà cung cấp — chọn NCC trước, hệ thống sẽ tự điền giá theo NCC đó.
            </MDTypography>
            <MDBox width="50%" minWidth={260}>
              <MDSelect
                labelType="non-sticky"
                labelText="Nhà cung cấp"
                placeholder="Chọn NCC…"
                options={supplierOptions}
                menuListMaxHeight="360px"
                maxMenuHeight={360}
                value={supplierOptions.find((o) => o.value === vendorId) || null}
                onChange={(o) => setVendorId(o?.value ?? null)}
              />
            </MDBox>
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              sx={{ alignSelf: "flex-start" }}
              disabled={vendorId == null || savingVendor}
              onClick={handleSaveVendor}
            >
              {savingVendor ? "Đang lưu…" : "Chọn NCC"}
            </MDButton>
          </MDBox>
        ) : (
          <MDBox pt={1} display="flex" flexDirection="column" gap={1.5}>
            <MDTypography variant="caption" color="text">
              NCC: <strong>{readiness?.vendorName || "—"}</strong>. Ô để trống ở dòng có giá gợi ý sẽ được tự điền
              theo gợi ý khi lưu.
            </MDTypography>
            <MDBox maxWidth={260}>
              <MDInput
                fullWidth
                labelType="non-sticky"
                labelText="Mã số thuế NCC báo (tuỳ chọn)"
                placeholder="Để trống = giữ nguyên"
                inputProps={{ maxLength: PROPOSAL_TAX_CODE_MAX }}
                value={taxCode}
                onChange={(e) => setTaxCode(e.target.value)}
                disabled={saving}
              />
            </MDBox>
            {rows.map((r) => {
              const key = proposalLineKey(r)
              const value = values[key] ?? ""
              const unit = byUnit(r)
              const suggested = unit ? r.suggestedUnitPrice : r.suggestedAmount
              const lineTotal = Number(value) > 0 ? (unit ? Number(value) * Number(r.quantity) : Number(value)) : r.suggestedAmount
              return (
                <MDBox key={key} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <MDBox flex={1} minWidth={200}>
                    <MDTypography variant="button" fontWeight="medium" display="block">
                      {r.itemName}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block">
                      {[r.itemCode, `SL ${r.quantity ?? "—"}`].filter(Boolean).join(" · ")}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color={r.status === "MISSING" ? "error" : "text"}
                      display="block"
                    >
                      {r.status === "AUTO_FILLABLE"
                        ? `Gợi ý: ${formatVND(suggested)}${unit ? " / đơn vị" : ""}`
                        : r.message || MISSING_PRICE_REASON_LABEL[r.missingReason] || "Chưa có giá"}
                    </MDTypography>
                    {r.status === "MISSING" && r.vendorPriceStatus === "EXPIRED" && r.vendorPrice != null && (
                      <MDTypography variant="caption" color="text" display="block">
                        Giá cũ của NCC: {formatVND(r.vendorPrice)}
                        {r.vendorPriceEffectiveDate ? ` (${formatDate(r.vendorPriceEffectiveDate)})` : ""}
                      </MDTypography>
                    )}
                    {r.status === "MISSING" && unit && Number(r.standardPrice) > 0 && (
                      <MDButton
                        size="small"
                        variant="text"
                        color="info"
                        sx={{ px: 0, minHeight: 0 }}
                        disabled={saving}
                        onClick={() => setValues((p) => ({ ...p, [key]: String(r.standardPrice) }))}
                      >
                        Dùng giá nguyên tắc {formatVND(r.standardPrice)}
                      </MDButton>
                    )}
                  </MDBox>
                  <MDBox width={200}>
                    <MDNumberInput
                      fullWidth
                      placeholder={unit ? "Đơn giá (VND)" : "Thành tiền (VND)"}
                      value={value}
                      error={r.status === "MISSING" && !(Number(value) > 0)}
                      onChange={(v) => setValues((p) => ({ ...p, [key]: v }))}
                      disabled={saving}
                    />
                  </MDBox>
                  <MDBox width={130} textAlign="right">
                    <MDTypography variant="button" fontWeight="bold">
                      {lineTotal != null ? formatVND(lineTotal) : "—"}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              )
            })}
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose} disabled={saving}>
          Để sau
        </MDButton>
        {!noVendor && (
          <MDButton variant="gradient" color="info" onClick={handleSave} disabled={!canSave}>
            {saving ? "Đang lưu…" : "Lưu giá"}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  )
}
